import React, { useState, useMemo } from 'react';
import { 
  BookOpen, Code2, Server, Database, Truck, User, MapPin, 
  Calendar, CheckCircle2, Copy, Check, Play, Search, 
  Layers, ArrowRight, Shield, Zap, RefreshCw, FileText, 
  ExternalLink, Download, Clock, Package, AlertCircle, ChevronDown, ChevronRight
} from 'lucide-react';

export default function DocumentationPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeLang, setActiveLang] = useState('curl'); // 'curl' | 'js' | 'python'
  const [copiedKey, setCopiedKey] = useState(null);
  const [testResponse, setTestResponse] = useState({});
  const [isTesting, setIsTesting] = useState({});
  const [expandedSections, setExpandedSections] = useState({
    intro: true,
    workflow: true,
    sec1: true,
    sec2: true,
    sec3: true,
    sec4: true,
    sec5: true,
    sec6: true,
    sec7: true,
    sec8: true
  });

  const handleCopy = (key, text) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const handleRunLiveTest = async (endpointKey, method, path, body = null) => {
    setIsTesting(prev => ({ ...prev, [endpointKey]: true }));
    try {
      const options = {
        method,
        headers: { 'Content-Type': 'application/json' }
      };
      if (body && method !== 'GET') {
        options.body = JSON.stringify(body);
      }
      const res = await fetch(path, options);
      const data = await res.json();
      setTestResponse(prev => ({
        ...prev,
        [endpointKey]: {
          status: res.status,
          ok: res.ok,
          data
        }
      }));
    } catch (err) {
      setTestResponse(prev => ({
        ...prev,
        [endpointKey]: {
          status: 'ERROR',
          ok: false,
          data: { error: err.message }
        }
      }));
    } finally {
      setIsTesting(prev => ({ ...prev, [endpointKey]: false }));
    }
  };

  const toggleSection = (sec) => {
    setExpandedSections(prev => ({ ...prev, [sec]: !prev[sec] }));
  };

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', background: '#0b0b0e', color: '#f2f2f6', fontFamily: 'Inter, system-ui, sans-serif', overflow: 'hidden' }}>
      
      {/* LEFT SIDEBAR NAVIGATION */}
      <aside style={{ width: '280px', height: '100%', borderRight: '1px solid #252530', background: '#111116', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
        
        {/* Sidebar Header */}
        <div style={{ padding: '16px', borderBottom: '1px solid #252530' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <div style={{ background: 'linear-gradient(135deg, #6366f1, #38bdf8)', padding: '6px', borderRadius: '8px', display: 'flex' }}>
              <Code2 size={18} color="#fff" />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '800' }}>Loops API & ERP Guide</h2>
              <span style={{ fontSize: '0.7rem', color: '#38bdf8', fontWeight: '600' }}>v2.4.0 REST & WebSockets</span>
            </div>
          </div>

          {/* Quick Search */}
          <div style={{ position: 'relative', marginTop: '10px' }}>
            <Search size={14} style={{ position: 'absolute', left: '10px', top: '9px', color: '#64748b' }} />
            <input
              type="text"
              placeholder="Filtrer les endpoints..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                background: '#181822',
                border: '1px solid #252530',
                borderRadius: '6px',
                padding: '6px 8px 6px 30px',
                fontSize: '0.75rem',
                color: '#fff',
                outline: 'none'
              }}
            />
          </div>
        </div>

        {/* Sidebar Nav Links */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 8px' }}>
          
          <div style={{ fontSize: '0.68rem', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', padding: '6px 8px', letterSpacing: '0.5px' }}>
            Guide d'intégration
          </div>
          <a href="#intro" className="doc-nav-link" style={navLinkStyle}>
            <Zap size={14} color="#6366f1" />
            <span>Introduction & Architecture</span>
          </a>
          <a href="#workflow" className="doc-nav-link" style={navLinkStyle}>
            <Layers size={14} color="#38bdf8" />
            <span>Cycle d'intégration ERP</span>
          </a>

          <div style={{ fontSize: '0.68rem', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', padding: '14px 8px 6px 8px', letterSpacing: '0.5px' }}>
            Endpoints par étape
          </div>

          <a href="#sec1" className="doc-nav-link" style={navLinkStyle}>
            <Server size={14} color="#22c55e" />
            <span>1. Dépôts & Flotte (Hubs/Trucks)</span>
          </a>
          <a href="#sec2" className="doc-nav-link" style={navLinkStyle}>
            <Database size={14} color="#f59e0b" />
            <span>2. Clients & Tournées Standard</span>
          </a>
          <a href="#sec3" className="doc-nav-link" style={navLinkStyle}>
            <Package size={14} color="#a855f7" />
            <span>3. Commandes & Livraisons (Stops)</span>
          </a>
          <a href="#sec4" className="doc-nav-link" style={navLinkStyle}>
            <MapPin size={14} color="#ec4899" />
            <span>4. Missions Journalières (Jobs)</span>
          </a>
          <a href="#sec5" className="doc-nav-link" style={navLinkStyle}>
            <Zap size={14} color="#38bdf8" />
            <span>5. Optimisation VRPTW & Matrice</span>
          </a>
          <a href="#sec6" className="doc-nav-link" style={navLinkStyle}>
            <Truck size={14} color="#10b981" />
            <span>6. Exécution & App Chauffeur</span>
          </a>
          <a href="#sec7" className="doc-nav-link" style={navLinkStyle}>
            <FileText size={14} color="#eab308" />
            <span>7. Audits & PlannedJobRecords</span>
          </a>
          <a href="#sec8" className="doc-nav-link" style={navLinkStyle}>
            <RefreshCw size={14} color="#8b5cf6" />
            <span>8. Événements Temps Réel (WS)</span>
          </a>
        </div>

        {/* Sidebar Footer */}
        <div style={{ padding: '12px 16px', borderTop: '1px solid #252530', background: '#0e0e12', fontSize: '0.72rem', color: '#64748b' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Base URL:</span>
            <code style={{ color: '#38bdf8', background: '#181822', padding: '2px 4px', borderRadius: '4px' }}>/api</code>
          </div>
        </div>

      </aside>

      {/* MAIN DOCUMENTATION CONTENT */}
      <main style={{ flex: 1, height: '100%', overflowY: 'auto', padding: '24px 36px', scrollBehavior: 'smooth' }}>
        
        {/* TOP HEADER CONTROLS */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', borderBottom: '1px solid #252530', paddingBottom: '16px' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.6rem', fontWeight: '900', letterSpacing: '-0.5px' }}>
              Guide d'Intégration ERP & Documentation API
            </h1>
            <p style={{ margin: '6px 0 0 0', color: '#9a9aaa', fontSize: '0.85rem' }}>
              Connectez votre ERP (SAP, Odoo, Navision, Winbiz, custom) à Loops pour automatiser l'expédition et l'optimisation des tournées.
            </p>
          </div>

          {/* LANGUAGE SELECTOR FOR EXAMPLES */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: '700', color: '#9a9aaa' }}>Exemples de code :</span>
            <div style={{ background: '#15151a', border: '1px solid #252530', borderRadius: '8px', padding: '2px', display: 'flex' }}>
              {[
                { id: 'curl', label: 'cURL' },
                { id: 'js', label: 'JavaScript' },
                { id: 'python', label: 'Python' }
              ].map(lang => (
                <button
                  key={lang.id}
                  onClick={() => setActiveLang(lang.id)}
                  style={{
                    background: activeLang === lang.id ? '#6366f1' : 'transparent',
                    color: activeLang === lang.id ? '#fff' : '#9a9aaa',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '4px 10px',
                    fontSize: '0.72rem',
                    fontWeight: '800',
                    cursor: 'pointer',
                    transition: 'all 0.15s'
                  }}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* SECTION: INTRODUCTION & ARCHITECTURE */}
        <section id="intro" style={sectionCardStyle}>
          <div onClick={() => toggleSection('intro')} style={sectionHeaderStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ ...badgeStyle, background: 'rgba(99, 102, 241, 0.15)', color: '#818cf8' }}>01</div>
              <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '800' }}>Introduction & Architecture Technique</h2>
            </div>
            {expandedSections.intro ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          </div>

          {expandedSections.intro && (
            <div style={{ marginTop: '16px', fontSize: '0.84rem', color: '#cbd5e1', lineHeight: '1.6' }}>
              <p>
                <strong>Loops</strong> est un moteur d'orchestration logistique et de dispatching temps réel conçu pour s'interfacer directement avec les ERPs d'entreprises de livraison et de transport.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px', marginTop: '14px' }}>
                <div style={featureBoxStyle}>
                  <div style={{ fontWeight: '800', color: '#38bdf8', marginBottom: '4px' }}>🗺️ Moteur Valhalla</div>
                  <div style={{ fontSize: '0.76rem', color: '#94a3b8' }}>Calculs de matrices de distances kilométriques et temporelles réelles basées sur OpenStreetMap.</div>
                </div>
                <div style={featureBoxStyle}>
                  <div style={{ fontWeight: '800', color: '#22c55e', marginBottom: '4px' }}>⚡ Solveur VROOM VRPTW</div>
                  <div style={{ fontSize: '0.76rem', color: '#94a3b8' }}>Optimisation avancée sous contraintes de fenêtres horaires, poids, volume, pauses et multi-shifts.</div>
                </div>
                <div style={featureBoxStyle}>
                  <div style={{ fontWeight: '800', color: '#f59e0b', marginBottom: '4px' }}>📡 Télémétrie WebSocket</div>
                  <div style={{ fontSize: '0.76rem', color: '#94a3b8' }}>Synchronisation bidirectionnelle instantanée entre l'ERP, l'interface du logisticien et l'application mobile livreur.</div>
                </div>
              </div>
            </div>
          )}
        </section>

        {/* SECTION: CYCLE D'INTÉGRATION ERP */}
        <section id="workflow" style={sectionCardStyle}>
          <div onClick={() => toggleSection('workflow')} style={sectionHeaderStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ ...badgeStyle, background: 'rgba(56, 189, 248, 0.15)', color: '#38bdf8' }}>02</div>
              <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '800' }}>Cycle d'Intégration Recommandé pour l'ERP</h2>
            </div>
            {expandedSections.workflow ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          </div>

          {expandedSections.workflow && (
            <div style={{ marginTop: '16px', fontSize: '0.84rem', color: '#cbd5e1' }}>
              <p style={{ margin: '0 0 16px 0' }}>
                Pour réussir l'intégration de votre ERP dans Loops, suivez ces <strong>8 étapes séquentielles</strong> :
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {[
                  { step: '1', title: 'Définir l’infrastructure de base', desc: 'Créer les hubs de départ/retour, les camions avec charges max, et les chauffeurs.', endpoint: 'POST /api/hubs · POST /api/trucks · POST /api/drivers' },
                  { step: '2', title: 'Synchroniser le référentiel clients', desc: 'Injecter les clients avec adresses géocodées, fenêtres horaires habituelles et roadbook.', endpoint: 'POST /api/clients · POST /api/clients/:id/time-windows' },
                  { step: '3', title: 'Configurer les modèles de tournées (Optionnel)', desc: 'Créer les tournées standards récurrentes pour pré-assigner des chauffeurs et véhicules.', endpoint: 'POST /api/rounds' },
                  { step: '4', title: 'Injecter les commandes du jour (Stops)', desc: 'Chaque matin ou veille, pousser les livraisons/ramassages avec date, poids, volume et créneaux.', endpoint: 'POST /api/stops OU POST /api/import/csv' },
                  { step: '5', title: 'Créer les missions journalières', desc: 'Créer les conteneurs de missions pour la date ciblée et y assigner les arrêts prévus.', endpoint: 'POST /api/jobs · POST /api/jobs/:id/assign-stop' },
                  { step: '6', title: 'Lancer l’optimisation de tournée (VRPTW)', desc: 'Générer l’ordonnancement optimal des arrêts respectant les horaires et capacités.', endpoint: 'POST /api/jobs/:id/optimize-preview · POST /api/cross-optimize-preview' },
                  { step: '7', title: 'Suivre l’exécution mobile du chauffeur', desc: 'Démarrage de mission, scan des colis, signatures, photos et clôture en direct.', endpoint: 'POST /api/jobs/:id/start · POST /api/stops/:id/complete' },
                  { step: '8', title: 'Récupérer les résultats pour la facturation & l’analyse', desc: 'Extraire les PlannedJobRecords, les temps réels, kilomètres réels et retours de consignes.', endpoint: 'GET /api/jobs/:id/planned-record · GET /api/bootstrap' }
                ].map((item, idx) => (
                  <div key={idx} style={{ background: '#111116', border: '1px solid #252530', borderRadius: '8px', padding: '12px 16px', display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                    <div style={{ background: '#6366f1', color: '#fff', width: '26px', height: '26px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '800', fontSize: '0.78rem', flexShrink: 0 }}>
                      {item.step}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '800', color: '#f2f2f6', fontSize: '0.85rem' }}>{item.title}</div>
                      <div style={{ fontSize: '0.78rem', color: '#94a3b8', margin: '2px 0 6px 0' }}>{item.desc}</div>
                      <code style={{ background: '#181822', color: '#38bdf8', padding: '2px 6px', borderRadius: '4px', fontSize: '0.72rem' }}>{item.endpoint}</code>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* SECTION 1: HUBS, TRUCKS & DRIVERS */}
        <section id="sec1" style={sectionCardStyle}>
          <div onClick={() => toggleSection('sec1')} style={sectionHeaderStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ ...badgeStyle, background: 'rgba(34, 197, 94, 0.15)', color: '#22c55e' }}>1</div>
              <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '800' }}>Étape 1 : Dépôts, Flotte de Véhicules & Chauffeurs</h2>
            </div>
            {expandedSections.sec1 ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          </div>

          {expandedSections.sec1 && (
            <div style={{ marginTop: '16px' }}>
              <p style={{ fontSize: '0.82rem', color: '#94a3b8', margin: '0 0 16px 0' }}>
                Ces ressources statiques définissent vos capacités logistiques. Vous les initialisez une seule fois ou les synchronisez périodiquement depuis votre ERP.
              </p>

              {/* Endpoint 1.1: Create Hub */}
              <EndpointCard
                method="POST"
                path="/api/hubs"
                title="Créer un Dépôt / Hub logistique"
                description="Crée un entrepôt central ou un point de départ/retour pour vos véhicules."
                activeLang={activeLang}
                copiedKey={copiedKey}
                onCopy={handleCopy}
                onRunTest={handleRunLiveTest}
                isTesting={isTesting['hubs_post']}
                testResponse={testResponse['hubs_post']}
                endpointKey="hubs_post"
                sampleBody={{
                  name: "Dépôt Central Lausanne",
                  street: "Chemin des Sauges 12",
                  postal_code: "1018",
                  city: "Lausanne",
                  country: "Suisse",
                  latitude: 46.5332,
                  longitude: 6.6214
                }}
              />

              {/* Endpoint 1.2: Create Truck */}
              <EndpointCard
                method="POST"
                path="/api/trucks"
                title="Créer un Véhicule / Camion"
                description="Enregistre un camion avec sa plaque, sa capacité de charge max (kg/m3), et ses coûts horaires/kilométriques."
                activeLang={activeLang}
                copiedKey={copiedKey}
                onCopy={handleCopy}
                onRunTest={handleRunLiveTest}
                isTesting={isTesting['trucks_post']}
                testResponse={testResponse['trucks_post']}
                endpointKey="trucks_post"
                sampleBody={{
                  name: "Iveco Daily Frigo #4",
                  plate: "VD 123456",
                  type: "Frigo",
                  max_payload_weight_kg: 1400,
                  max_payload_volume_m3: 16.5,
                  cost_per_km: 0.85,
                  cost_per_hour: 45.0,
                  fixed_cost: 30.0,
                  start_hub_id: 1,
                  end_hub_id: 1,
                  planned_start_time: "07:30",
                  planned_end_time: "17:00"
                }}
              />

              {/* Endpoint 1.3: Create Driver */}
              <EndpointCard
                method="POST"
                path="/api/drivers"
                title="Créer un Chauffeur"
                description="Enregistre un livreur avec son numéro de téléphone et son véhicule par défaut."
                activeLang={activeLang}
                copiedKey={copiedKey}
                onCopy={handleCopy}
                onRunTest={handleRunLiveTest}
                isTesting={isTesting['drivers_post']}
                testResponse={testResponse['drivers_post']}
                endpointKey="drivers_post"
                sampleBody={{
                  first_name: "Jean",
                  last_name: "Dupont",
                  phone: "+41 79 123 45 67",
                  email: "j.dupont@transport.ch",
                  default_truck_id: 1
                }}
              />
            </div>
          )}
        </section>

        {/* SECTION 2: CLIENTS & ROUND TEMPLATES */}
        <section id="sec2" style={sectionCardStyle}>
          <div onClick={() => toggleSection('sec2')} style={sectionHeaderStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ ...badgeStyle, background: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b' }}>2</div>
              <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '800' }}>Étape 2 : Référentiel Clients & Modèles de Tournées</h2>
            </div>
            {expandedSections.sec2 ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          </div>

          {expandedSections.sec2 && (
            <div style={{ marginTop: '16px' }}>
              <p style={{ fontSize: '0.82rem', color: '#94a3b8', margin: '0 0 16px 0' }}>
                Synchronisez votre base client depuis l'ERP. Le serveur Loops géocode automatiquement les adresses si les coordonnées GPS ne sont pas fournies.
              </p>

              {/* Endpoint 2.1: Create Client */}
              <EndpointCard
                method="POST"
                path="/api/clients"
                title="Créer ou Mettre à jour un Client"
                description="Injecte un client destinataire avec ses coordonnées, instructions d'accès (roadbook) et temps de service moyen."
                activeLang={activeLang}
                copiedKey={copiedKey}
                onCopy={handleCopy}
                onRunTest={handleRunLiveTest}
                isTesting={isTesting['clients_post']}
                testResponse={testResponse['clients_post']}
                endpointKey="clients_post"
                sampleBody={{
                  code: "CLI-10042",
                  name: "Restaurant Le Rivage",
                  street: "Quai Perdonnet 19",
                  postal_code: "1800",
                  city: "Vevey",
                  country: "Suisse",
                  latitude: 46.4598,
                  longitude: 6.8452,
                  phone: "+41 21 921 00 00",
                  roadbook: "Livraison par la porte arrière côté ruelle. Sonnette 'Cuisine'.",
                  driver_postit: "Ne pas bloquer l'allée des secours.",
                  default_service_time_mins: 12
                }}
              />

              {/* Endpoint 2.2: Add Client Time Window */}
              <EndpointCard
                method="POST"
                path="/api/clients/1/time-windows"
                title="Ajouter une Fenêtre Horaire au Client"
                description="Définit les plages d'ouverture acceptables pour le client selon les jours de la semaine (utilisé par le solveur VRPTW)."
                activeLang={activeLang}
                copiedKey={copiedKey}
                onCopy={handleCopy}
                onRunTest={handleRunLiveTest}
                isTesting={isTesting['tw_post']}
                testResponse={testResponse['tw_post']}
                endpointKey="tw_post"
                sampleBody={{
                  days_of_week: "Lundi,Mardi,Mercredi,Jeudi,Vendredi",
                  time_window_start: "08:00:00",
                  time_window_end: "11:30:00"
                }}
              />

              {/* Endpoint 2.3: Create Round Template */}
              <EndpointCard
                method="POST"
                path="/api/rounds"
                title="Créer un Modèle de Tournée (Round)"
                description="Crée une tournée type pour regrouper logiquement des clients par zone géographique ou secteur."
                activeLang={activeLang}
                copiedKey={copiedKey}
                onCopy={handleCopy}
                onRunTest={handleRunLiveTest}
                isTesting={isTesting['rounds_post']}
                testResponse={testResponse['rounds_post']}
                endpointKey="rounds_post"
                sampleBody={{
                  name: "Riviera - Matin",
                  code: "RIV-AM",
                  color: "#22c55e",
                  default_driver_id: 1,
                  default_truck_id: 1,
                  default_start_hub_id: 1,
                  default_end_hub_id: 1,
                  default_start_time: "07:30",
                  default_end_time: "12:00"
                }}
              />
            </div>
          )}
        </section>

        {/* SECTION 3: ORDERS & STOPS */}
        <section id="sec3" style={sectionCardStyle}>
          <div onClick={() => toggleSection('sec3')} style={sectionHeaderStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ ...badgeStyle, background: 'rgba(168, 85, 247, 0.15)', color: '#a855f7' }}>3</div>
              <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '800' }}>Étape 3 : Commandes & Arrêts de Livraison (Stops)</h2>
            </div>
            {expandedSections.sec3 ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          </div>

          {expandedSections.sec3 && (
            <div style={{ marginTop: '16px' }}>
              <p style={{ fontSize: '0.82rem', color: '#94a3b8', margin: '0 0 16px 0' }}>
                Chaque matin ou la veille, votre ERP pousse les bons de livraisons ou de ramassage vers Loops. Vous pouvez les pousser un par un ou par lot CSV.
              </p>

              {/* Endpoint 3.1: Create Stop */}
              <EndpointCard
                method="POST"
                path="/api/stops"
                title="Créer un Arrêt de Livraison (Stop)"
                description="Injecte un point de passage avec sa date de livraison, son poids, volume, valeur CA et instructions spécifiques."
                activeLang={activeLang}
                copiedKey={copiedKey}
                onCopy={handleCopy}
                onRunTest={handleRunLiveTest}
                isTesting={isTesting['stops_post']}
                testResponse={testResponse['stops_post']}
                endpointKey="stops_post"
                sampleBody={{
                  client_id: 1,
                  delivery_date: "2026-08-15",
                  type: "delivery",
                  weight_kg: 180.5,
                  volume_m3: 1.2,
                  revenue: 350.0,
                  service_time_mins: 15,
                  instructions: "Livrer 4 cartons de vin + 2 bacs frais.",
                  time_windows: [{ start: "08:30:00", end: "11:00:00" }]
                }}
              />

              {/* Endpoint 3.2: Bulk CSV Import */}
              <EndpointCard
                method="POST"
                path="/api/import/csv"
                title="Import Massif de Commandes (CSV / ERP Data)"
                description="Importe un fichier CSV ou une chaîne de données tabulaires pour insérer des centaines de livraisons en un seul appel."
                activeLang={activeLang}
                copiedKey={copiedKey}
                onCopy={handleCopy}
                onRunTest={handleRunLiveTest}
                isTesting={isTesting['import_csv']}
                testResponse={testResponse['import_csv']}
                endpointKey="import_csv"
                sampleBody={{
                  csvData: "client_code,date,weight_kg,volume_m3,revenue,instructions\nCLI-10042,2026-08-15,120,0.8,240,Commande #98421\nCLI-10088,2026-08-15,45,0.3,110,Commande #98422"
                }}
              />

              {/* Endpoint 3.3: Set Stop Precedence / Dependency */}
              <EndpointCard
                method="POST"
                path="/api/stop-dependencies"
                title="Définir une Dépendance (Ex: Ramassage avant Livraison)"
                description="Force une contrainte de précédence (Pickup & Delivery) : le stop A doit impérativement être effectué avant le stop B."
                activeLang={activeLang}
                copiedKey={copiedKey}
                onCopy={handleCopy}
                onRunTest={handleRunLiveTest}
                isTesting={isTesting['dep_post']}
                testResponse={testResponse['dep_post']}
                endpointKey="dep_post"
                sampleBody={{
                  stop_id: 2,
                  depends_on_stop_id: 1,
                  type: "pickup_before_delivery"
                }}
              />
            </div>
          )}
        </section>

        {/* SECTION 4: JOBS & DISPATCH */}
        <section id="sec4" style={sectionCardStyle}>
          <div onClick={() => toggleSection('sec4')} style={sectionHeaderStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ ...badgeStyle, background: 'rgba(236, 72, 153, 0.15)', color: '#ec4899' }}>4</div>
              <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '800' }}>Étape 4 : Création des Missions Journalières (Jobs)</h2>
            </div>
            {expandedSections.sec4 ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          </div>

          {expandedSections.sec4 && (
            <div style={{ marginTop: '16px' }}>
              <p style={{ fontSize: '0.82rem', color: '#94a3b8', margin: '0 0 16px 0' }}>
                Un <strong>Job</strong> représente la mission réelle d'un camion sur une date donnée. Vous pouvez créer un job manuellement ou à partir d'un modèle de tournée.
              </p>

              {/* Endpoint 4.1: Create Job */}
              <EndpointCard
                method="POST"
                path="/api/jobs"
                title="Créer une Mission (Job)"
                description="Instancie une nouvelle mission avec son camion, son chauffeur et son heure de départ prévue."
                activeLang={activeLang}
                copiedKey={copiedKey}
                onCopy={handleCopy}
                onRunTest={handleRunLiveTest}
                isTesting={isTesting['jobs_post']}
                testResponse={testResponse['jobs_post']}
                endpointKey="jobs_post"
                sampleBody={{
                  display_name: "Tournée Riviera - 15 Août",
                  round_id: 1,
                  driver_id: 1,
                  truck_id: 1,
                  start_hub_id: 1,
                  end_hub_id: 1,
                  estimated_departure: "07:30"
                }}
              />

              {/* Endpoint 4.2: Assign Stop to Job */}
              <EndpointCard
                method="POST"
                path="/api/jobs/1/assign-stop"
                title="Assigner un Arrêt à une Mission"
                description="Assigne un arrêt non affecté à une mission spécifique et recalcule automatiquement les métriques de trajet."
                activeLang={activeLang}
                copiedKey={copiedKey}
                onCopy={handleCopy}
                onRunTest={handleRunLiveTest}
                isTesting={isTesting['assign_post']}
                testResponse={testResponse['assign_post']}
                endpointKey="assign_post"
                sampleBody={{
                  stopId: 1
                }}
              />

              {/* Endpoint 4.3: Add Driver Pause */}
              <EndpointCard
                method="POST"
                path="/api/jobs/1/pauses"
                title="Ajouter une Pause Chauffeur (Repas / Réglementaire)"
                description="Insère un créneau de pause obligatoire dans le planning de la mission (ex: pause midi 12h00 - 12h45)."
                activeLang={activeLang}
                copiedKey={copiedKey}
                onCopy={handleCopy}
                onRunTest={handleRunLiveTest}
                isTesting={isTesting['pauses_post']}
                testResponse={testResponse['pauses_post']}
                endpointKey="pauses_post"
                sampleBody={{
                  name: "Pause Déjeuner",
                  start_time: "12:00",
                  end_time: "12:45",
                  duration_sec: 2700
                }}
              />
            </div>
          )}
        </section>

        {/* SECTION 5: VRPTW OPTIMIZATION ENGINE */}
        <section id="sec5" style={sectionCardStyle}>
          <div onClick={() => toggleSection('sec5')} style={sectionHeaderStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ ...badgeStyle, background: 'rgba(56, 189, 248, 0.15)', color: '#38bdf8' }}>5</div>
              <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '800' }}>Étape 5 : Moteur d'Optimisation VRPTW & Croisée</h2>
            </div>
            {expandedSections.sec5 ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          </div>

          {expandedSections.sec5 && (
            <div style={{ marginTop: '16px' }}>
              <p style={{ fontSize: '0.82rem', color: '#94a3b8', margin: '0 0 16px 0' }}>
                Loops intègre le solveur <strong>VROOM</strong> couplé à la matrice <strong>Valhalla</strong> pour résoudre les problèmes de tournées avec fenêtres horaires (VRPTW) en quelques millisecondes.
              </p>

              {/* Endpoint 5.1: Single Job Preview */}
              <EndpointCard
                method="POST"
                path="/api/jobs/1/optimize-preview"
                title="Aperçu d'Optimisation d'une Mission (Single VRPTW)"
                description="Simule l'ordonnancement optimal des arrêts d'une mission et retourne les gains kilométriques et temporels sans modifier la base."
                activeLang={activeLang}
                copiedKey={copiedKey}
                onCopy={handleCopy}
                onRunTest={handleRunLiveTest}
                isTesting={isTesting['opti_preview']}
                testResponse={testResponse['opti_preview']}
                endpointKey="opti_preview"
                sampleBody={{
                  flexibleStart: true,
                  enforceTW: true,
                  enforceWeight: true,
                  enforceVolume: true
                }}
              />

              {/* Endpoint 5.2: Apply Single Optimization */}
              <EndpointCard
                method="POST"
                path="/api/jobs/1/apply-optimization"
                title="Appliquer l'Optimisation de la Mission"
                description="Enregistre la séquence optimale calculée, met à jour les heures estimées d'arrivée à chaque client et trace la polyline."
                activeLang={activeLang}
                copiedKey={copiedKey}
                onCopy={handleCopy}
                onRunTest={handleRunLiveTest}
                isTesting={isTesting['opti_apply']}
                testResponse={testResponse['opti_apply']}
                endpointKey="opti_apply"
                sampleBody={{
                  optimizedOrder: [1, 4, 2, 3],
                  estimatedCost: 145.20,
                  estimatedLengthKm: 68.4,
                  estimatedDurationSec: 16200
                }}
              />

              {/* Endpoint 5.3: Cross Optimization Preview */}
              <EndpointCard
                method="POST"
                path="/api/cross-optimize-preview"
                title="Aperçu d'Optimisation Croisée Multi-Camions (Cross-Optimization)"
                description="Répartit intelligemment un ensemble d'arrêts et de missions entre plusieurs camions et plages horaires (shifts) simultanément."
                activeLang={activeLang}
                copiedKey={copiedKey}
                onCopy={handleCopy}
                onRunTest={handleRunLiveTest}
                isTesting={isTesting['cross_preview']}
                testResponse={testResponse['cross_preview']}
                endpointKey="cross_preview"
                sampleBody={{
                  selectedJobIds: [1, 2],
                  includeUnassigned: true,
                  truckConfigs: [
                    { id: 1, startTime: "07:30", endTime: "17:00", driverId: 1, shifts: [{ startTime: "07:30", endTime: "17:00" }] },
                    { id: 2, startTime: "08:00", endTime: "16:30", driverId: 2, shifts: [{ startTime: "08:00", endTime: "16:30" }] }
                  ],
                  enforceTW: true,
                  flexibleStart: true,
                  enforceWeight: true,
                  enforceVolume: true
                }}
              />
            </div>
          )}
        </section>

        {/* SECTION 6: DRIVER EXECUTION & MOBILE APP */}
        <section id="sec6" style={sectionCardStyle}>
          <div onClick={() => toggleSection('sec6')} style={sectionHeaderStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ ...badgeStyle, background: 'rgba(16, 185, 129, 0.15)', color: '#10b981' }}>6</div>
              <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '800' }}>Étape 6 : Exécution Mobile du Livreur & Télémétrie</h2>
            </div>
            {expandedSections.sec6 ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          </div>

          {expandedSections.sec6 && (
            <div style={{ marginTop: '16px' }}>
              <p style={{ fontSize: '0.82rem', color: '#94a3b8', margin: '0 0 16px 0' }}>
                L'application mobile livreur (PWA) communique via ces endpoints pour signaler les départs, arrivées, preuves de livraison et retours de consignes.
              </p>

              {/* Endpoint 6.1: Start Job */}
              <EndpointCard
                method="POST"
                path="/api/jobs/1/start"
                title="Démarrer la Mission (Start Job)"
                description="Le chauffeur démarre sa tournée. Loops crée automatiquement un snapshot immutable PlannedJobRecord pour l'audit ultérieur."
                activeLang={activeLang}
                copiedKey={copiedKey}
                onCopy={handleCopy}
                onRunTest={handleRunLiveTest}
                isTesting={isTesting['job_start']}
                testResponse={testResponse['job_start']}
                endpointKey="job_start"
                sampleBody={{
                  start_km: 145200.0,
                  departure_time: new Date().toISOString()
                }}
              />

              {/* Endpoint 6.2: Stop Arrival */}
              <EndpointCard
                method="POST"
                path="/api/stops/1/arrival"
                title="Signaler l'Arrivée chez le Client"
                description="Enregistre l'horodatage précis d'arrivée du véhicule sur le site du client."
                activeLang={activeLang}
                copiedKey={copiedKey}
                onCopy={handleCopy}
                onRunTest={handleRunLiveTest}
                isTesting={isTesting['stop_arrival']}
                testResponse={testResponse['stop_arrival']}
                endpointKey="stop_arrival"
                sampleBody={{
                  actual_arrival: new Date().toISOString()
                }}
              />

              {/* Endpoint 6.3: Complete Stop */}
              <EndpointCard
                method="POST"
                path="/api/stops/1/complete"
                title="Clôturer la Livraison (POD & Signature)"
                description="Valide la livraison, enregistre le paiement éventuel, les notes du chauffeur et déclenche l'événement temps réel."
                activeLang={activeLang}
                copiedKey={copiedKey}
                onCopy={handleCopy}
                onRunTest={handleRunLiveTest}
                isTesting={isTesting['stop_complete']}
                testResponse={testResponse['stop_complete']}
                endpointKey="stop_complete"
                sampleBody={{
                  status: "delivered", // 'delivered' | 'failed' | 'partial'
                  driver_notes: "Marchandise réceptionnée par le chef de cuisine.",
                  payment_type: "cash",
                  amount_paid: 150.0,
                  actual_departure: new Date().toISOString()
                }}
              />

              {/* Endpoint 6.4: Return Ticket */}
              <EndpointCard
                method="POST"
                path="/api/stops/1/return-tickets"
                title="Enregistrer un Bon de Retour / Consignes"
                description="Génère un bon de reprise d'emballages, palettes Europe, bacs ou invendus chez le client."
                activeLang={activeLang}
                copiedKey={copiedKey}
                onCopy={handleCopy}
                onRunTest={handleRunLiveTest}
                isTesting={isTesting['return_ticket']}
                testResponse={testResponse['return_ticket']}
                endpointKey="return_ticket"
                sampleBody={{
                  notes: "Reprise de 4 caisses plastiques vides.",
                  lines: [
                    { object_id: 1, amount: 4, unit_price: 5.0, total_price: 20.0 }
                  ]
                }}
              />
            </div>
          )}
        </section>

        {/* SECTION 7: AUDITS & PLANNED RECORDS */}
        <section id="sec7" style={sectionCardStyle}>
          <div onClick={() => toggleSection('sec7')} style={sectionHeaderStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ ...badgeStyle, background: 'rgba(234, 179, 8, 0.15)', color: '#eab308' }}>7</div>
              <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '800' }}>Étape 7 : Audits, PlannedJobRecords & Extraction BI</h2>
            </div>
            {expandedSections.sec7 ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          </div>

          {expandedSections.sec7 && (
            <div style={{ marginTop: '16px' }}>
              <p style={{ fontSize: '0.82rem', color: '#94a3b8', margin: '0 0 16px 0' }}>
                Comparez le <strong>prévu vs réalisé</strong> pour vos calculs de rentabilité et exportez les données vers vos outils de Business Intelligence.
              </p>

              {/* Endpoint 7.1: Planned Job Record */}
              <EndpointCard
                method="GET"
                path="/api/jobs/1/planned-record"
                title="Obtenir le Snapshot Prévu d'une Mission (PlannedRecord)"
                description="Retourne le JSON complet de ce qui avait été calculé/planifié au moment exact où le chauffeur a cliqué sur 'Démarrer'."
                activeLang={activeLang}
                copiedKey={copiedKey}
                onCopy={handleCopy}
                onRunTest={handleRunLiveTest}
                isTesting={isTesting['planned_record']}
                testResponse={testResponse['planned_record']}
                endpointKey="planned_record"
              />

              {/* Endpoint 7.2: Bootstrap All Data */}
              <EndpointCard
                method="GET"
                path="/api/bootstrap?all=true"
                title="Extraction Globale pour ERP (Bootstrap All)"
                description="Renvoie l'ensemble du jeu de données (missions, arrêts, camions, chauffeurs, clients, adresses) pour synchronisation complète."
                activeLang={activeLang}
                copiedKey={copiedKey}
                onCopy={handleCopy}
                onRunTest={handleRunLiveTest}
                isTesting={isTesting['bootstrap_all']}
                testResponse={testResponse['bootstrap_all']}
                endpointKey="bootstrap_all"
              />

              {/* Endpoint 7.3: Export CSV */}
              <EndpointCard
                method="GET"
                path="/api/stops/export-csv"
                title="Export CSV des Livraisons Réalisées"
                description="Génère un flux CSV prêt pour l'intégration comptable et la facturation dans votre ERP."
                activeLang={activeLang}
                copiedKey={copiedKey}
                onCopy={handleCopy}
                onRunTest={handleRunLiveTest}
                isTesting={isTesting['export_csv']}
                testResponse={testResponse['export_csv']}
                endpointKey="export_csv"
              />
            </div>
          )}
        </section>

        {/* SECTION 8: REALTIME WEBSOCKETS */}
        <section id="sec8" style={sectionCardStyle}>
          <div onClick={() => toggleSection('sec8')} style={sectionHeaderStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ ...badgeStyle, background: 'rgba(139, 92, 246, 0.15)', color: '#8b5cf6' }}>8</div>
              <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '800' }}>Étape 8 : Flux Temps Réel WebSockets (ws://)</h2>
            </div>
            {expandedSections.sec8 ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          </div>

          {expandedSections.sec8 && (
            <div style={{ marginTop: '16px', fontSize: '0.84rem', color: '#cbd5e1' }}>
              <p style={{ margin: '0 0 12px 0' }}>
                Connectez un client WebSocket sur <code>ws://&lt;host&gt;:3001/ws</code> pour recevoir les événements dès qu'un chauffeur progresse sur le terrain.
              </p>

              <div style={{ background: '#111116', border: '1px solid #252530', borderRadius: '8px', padding: '14px', marginBottom: '14px' }}>
                <div style={{ fontWeight: '800', color: '#38bdf8', marginBottom: '8px', fontSize: '0.85rem' }}>Exemple d'Événement Émis par Loops :</div>
                <pre style={{ margin: 0, fontSize: '0.75rem', color: '#a5f3fc', overflowX: 'auto', background: '#0b0b0e', padding: '12px', borderRadius: '6px' }}>
{JSON.stringify({
  type: "REALTIME_DRIVER_UPDATE",
  event_type: "STOP_COMPLETED",
  timestamp: "2026-08-15T10:45:12.342Z",
  job_id: 1,
  stop_id: 42,
  client_name: "Restaurant Le Rivage",
  status: "delivered",
  driver_name: "Jean Dupont",
  truck_plate: "VD 123456"
}, null, 2)}
                </pre>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div style={{ background: '#181822', padding: '10px', borderRadius: '6px', fontSize: '0.78rem' }}>
                  <span style={{ color: '#22c55e', fontWeight: '800' }}>JOB_STARTED</span> : Le chauffeur a démarré sa mission
                </div>
                <div style={{ background: '#181822', padding: '10px', borderRadius: '6px', fontSize: '0.78rem' }}>
                  <span style={{ color: '#38bdf8', fontWeight: '800' }}>DRIVER_ARRIVED</span> : Le camion est arrivé chez le client
                </div>
                <div style={{ background: '#181822', padding: '10px', borderRadius: '6px', fontSize: '0.78rem' }}>
                  <span style={{ color: '#a855f7', fontWeight: '800' }}>STOP_COMPLETED</span> : Livraison validée avec signature/POD
                </div>
                <div style={{ background: '#181822', padding: '10px', borderRadius: '6px', fontSize: '0.78rem' }}>
                  <span style={{ color: '#f59e0b', fontWeight: '800' }}>JOB_FINISHED</span> : Le camion est rentré au dépôt central
                </div>
              </div>
            </div>
          )}
        </section>

      </main>
    </div>
  );
}

// Reusable Endpoint Documentation Card with Interactive Testing
function EndpointCard({
  method,
  path,
  title,
  description,
  activeLang,
  copiedKey,
  onCopy,
  onRunTest,
  isTesting,
  testResponse,
  endpointKey,
  sampleBody
}) {
  const methodColor = method === 'GET' ? '#38bdf8' : method === 'POST' ? '#22c55e' : method === 'PUT' ? '#f59e0b' : '#ef4444';
  const methodBg = method === 'GET' ? 'rgba(56, 189, 248, 0.15)' : method === 'POST' ? 'rgba(34, 197, 94, 0.15)' : method === 'PUT' ? 'rgba(245, 158, 11, 0.15)' : 'rgba(239, 68, 68, 0.15)';

  const codeSnippet = useMemo(() => {
    if (activeLang === 'curl') {
      if (method === 'GET') {
        return `curl -X GET "http://localhost:3001${path}" \\
  -H "Accept: application/json"`;
      }
      return `curl -X ${method} "http://localhost:3001${path}" \\
  -H "Content-Type: application/json" \\
  -d '${JSON.stringify(sampleBody || {}, null, 2)}'`;
    }

    if (activeLang === 'js') {
      if (method === 'GET') {
        return `const res = await fetch('http://localhost:3001${path}');
const data = await res.json();
console.log(data);`;
      }
      return `const res = await fetch('http://localhost:3001${path}', {
  method: '${method}',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(${JSON.stringify(sampleBody || {}, null, 2)})
});
const data = await res.json();
console.log(data);`;
    }

    if (activeLang === 'python') {
      if (method === 'GET') {
        return `import requests

res = requests.get('http://localhost:3001${path}')
print(res.json())`;
      }
      return `import requests

payload = ${JSON.stringify(sampleBody || {}, null, 2)}
res = requests.${method.toLowerCase()}('http://localhost:3001${path}', json=payload)
print(res.json())`;
    }

    return '';
  }, [activeLang, method, path, sampleBody]);

  return (
    <div style={{ background: '#15151a', border: '1px solid #252530', borderRadius: '10px', marginBottom: '16px', overflow: 'hidden' }}>
      
      {/* CARD HEADER */}
      <div style={{ padding: '12px 16px', background: '#111116', borderBottom: '1px solid #252530', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ background: methodBg, color: methodColor, padding: '3px 8px', borderRadius: '6px', fontSize: '0.72rem', fontWeight: '900', letterSpacing: '0.5px' }}>
            {method}
          </span>
          <code style={{ fontSize: '0.85rem', color: '#f2f2f6', fontWeight: '700' }}>{path}</code>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* RUN LIVE TEST BUTTON */}
          <button
            onClick={() => onRunTest(endpointKey, method, path, sampleBody)}
            disabled={isTesting}
            style={{
              background: '#181822',
              border: '1px solid #334155',
              color: '#38bdf8',
              borderRadius: '6px',
              padding: '4px 10px',
              fontSize: '0.72rem',
              fontWeight: '700',
              cursor: isTesting ? 'wait' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <Play size={12} fill="#38bdf8" />
            <span>{isTesting ? 'Exécution...' : 'Tester en direct'}</span>
          </button>

          {/* COPY SNIPPET BUTTON */}
          <button
            onClick={() => onCopy(endpointKey, codeSnippet)}
            style={{
              background: '#181822',
              border: '1px solid #334155',
              color: copiedKey === endpointKey ? '#22c55e' : '#9a9aaa',
              borderRadius: '6px',
              padding: '4px 10px',
              fontSize: '0.72rem',
              fontWeight: '700',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            {copiedKey === endpointKey ? <Check size={12} /> : <Copy size={12} />}
            <span>{copiedKey === endpointKey ? 'Copié !' : 'Copier'}</span>
          </button>
        </div>
      </div>

      {/* CARD BODY */}
      <div style={{ padding: '14px 16px' }}>
        <div style={{ fontWeight: '700', fontSize: '0.88rem', color: '#f2f2f6', marginBottom: '4px' }}>
          {title}
        </div>
        <p style={{ margin: '0 0 12px 0', fontSize: '0.78rem', color: '#94a3b8' }}>
          {description}
        </p>

        {/* CODE PREVIEW */}
        <div style={{ position: 'relative' }}>
          <pre style={{ margin: 0, padding: '12px', background: '#0b0b0e', border: '1px solid #252530', borderRadius: '6px', fontSize: '0.74rem', color: '#e2e8f0', overflowX: 'auto', lineHeight: '1.5' }}>
            {codeSnippet}
          </pre>
        </div>

        {/* LIVE RESPONSE INSPECTOR */}
        {testResponse && (
          <div style={{ marginTop: '12px', background: testResponse.ok ? '#0b130f' : '#180e0e', border: `1px solid ${testResponse.ok ? '#22c55e' : '#ef4444'}`, borderRadius: '6px', padding: '10px 12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <span style={{ fontSize: '0.72rem', fontWeight: '800', color: testResponse.ok ? '#4ade80' : '#fca5a5' }}>
                Réponse API {testResponse.status} :
              </span>
            </div>
            <pre style={{ margin: 0, fontSize: '0.72rem', color: testResponse.ok ? '#86efac' : '#fca5a5', maxHeight: '180px', overflowY: 'auto' }}>
              {JSON.stringify(testResponse.data, null, 2)}
            </pre>
          </div>
        )}
      </div>

    </div>
  );
}

// Styles
const navLinkStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 10px',
  borderRadius: '6px',
  fontSize: '0.75rem',
  fontWeight: '600',
  color: '#94a3b8',
  textDecoration: 'none',
  marginBottom: '2px',
  transition: 'all 0.15s ease'
};

const sectionCardStyle = {
  background: '#15151a',
  border: '1px solid #252530',
  borderRadius: '12px',
  padding: '16px 20px',
  marginBottom: '20px'
};

const sectionHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  cursor: 'pointer',
  userSelect: 'none'
};

const badgeStyle = {
  width: '26px',
  height: '26px',
  borderRadius: '6px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: '800',
  fontSize: '0.75rem'
};

const featureBoxStyle = {
  background: '#111116',
  border: '1px solid #252530',
  borderRadius: '8px',
  padding: '12px 14px'
};
